
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { ProjectForm } from "@/components/projects/ProjectForm";
import { GeneralTaskForm } from "@/components/tasks/GeneralTaskForm";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Header } from "@/components/Header";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Eye, Calendar, Users, CheckCircle, Clock, AlertTriangle } from "lucide-react";

const ProjectDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganization();
  const [newProjectOpen, setNewProjectOpen] = useState(false);
  const [newTaskOpen, setNewTaskOpen] = useState(false);

  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ["projects", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");
      
      const { data, error } = await supabase.functions.invoke('project-operations', {
        body: {
          action: "get",
          organizationId: currentOrganization.id,
        },
      });
      
      if (error) throw error;
      return data.projects || [];
    },
    enabled: !!currentOrganization,
  });

  const { data: recentProjectTasks } = useQuery({
    queryKey: ["recent-project-tasks", currentOrganization?.id],
    queryFn: async () => {
      if (!currentOrganization) throw new Error("No organization selected");

      // Get all project tasks from all projects in the organization
      const { data: orgProjects } = await supabase.functions.invoke('project-operations', {
        body: {
          action: "get",
          organizationId: currentOrganization.id,
        },
      });

      if (!orgProjects?.projects?.length) return [];

      // Fetch project tasks for all organization projects
      const allProjectTasks: any[] = [];
      
      for (const project of orgProjects.projects) {
        const { data: trackingData } = await supabase.functions.invoke('project-tracking-operations', {
          body: {
            action: 'getTrackingData',
            projectId: project.id,
            organizationId: currentOrganization.id,
          },
        });

        if (trackingData?.tasks) {
          // Add project name to each task
          const tasksWithProject = trackingData.tasks.map((task: any) => ({
            ...task,
            project_name: project.name
          }));
          allProjectTasks.push(...tasksWithProject);
        }
      }
      
      // Sort by created_at and get recent 10
      return allProjectTasks
        .sort((a: any, b: any) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);
    },
    enabled: !!currentOrganization,
  });

  const getProjectProgress = (project: any) => {
    if (!project.project_milestones?.length) return 0;
    const completed = project.project_milestones.filter((m: any) => m.completed).length;
    return Math.round((completed / project.project_milestones.length) * 100);
  };

  const getProjectStatus = (project: any) => {
    const progress = getProjectProgress(project);
    if (progress === 100) return { status: "Completed", color: "bg-green-500" };
    if (progress > 50) return { status: "On Track", color: "bg-blue-500" };
    if (progress > 0) return { status: "In Progress", color: "bg-yellow-500" };
    return { status: "Not Started", color: "bg-gray-500" };
  };

  const stats = {
    totalProjects: projects?.length || 0,
    activeProjects: projects?.filter((p: any) => getProjectProgress(p) > 0 && getProjectProgress(p) < 100).length || 0,
    completedProjects: projects?.filter((p: any) => getProjectProgress(p) === 100).length || 0,
    overdueTasks: recentProjectTasks?.filter((t: any) => 
      t.due_date && new Date(t.due_date) < new Date() && t.status !== 'completed'
    ).length || 0,
  };

  if (!currentOrganization) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="container mx-auto py-6">
          <div className="text-center p-8">
            <h2 className="text-2xl font-bold mb-4">No Organization Selected</h2>
            <p className="text-muted-foreground">Please select an organization to view the project dashboard.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="container mx-auto py-6 space-y-6">
        {/* Header with Quick Actions */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">Project Dashboard</h1>
            <p className="text-muted-foreground">{currentOrganization.name}</p>
          </div>
          <div className="flex gap-2">
            <Dialog open={newTaskOpen} onOpenChange={setNewTaskOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Plus className="h-4 w-4 mr-2" />
                  Quick Task
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create Quick Task</DialogTitle>
                </DialogHeader>
                <GeneralTaskForm 
                  onSuccess={() => {
                    toast({
                      title: "Success",
                      description: "General task created successfully"
                    });
                    setNewTaskOpen(false);
                    queryClient.invalidateQueries({ queryKey: ["tasks"] });
                  }} 
                />
              </DialogContent>
            </Dialog>
            
            <Dialog open={newProjectOpen} onOpenChange={setNewProjectOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  New Project
                </Button>
              </DialogTrigger>
              <DialogContent className="max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Create New Project</DialogTitle>
                </DialogHeader>
                <ProjectForm onSuccess={() => {
                  toast({
                    title: "Success",
                    description: "Project created successfully"
                  });
                  setNewProjectOpen(false);
                  queryClient.invalidateQueries({ queryKey: ["projects"] });
                }} />
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Total Projects</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalProjects}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Projects</CardTitle>
              <Clock className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.activeProjects}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Completed</CardTitle>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.completedProjects}</div>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Overdue Tasks</CardTitle>
              <AlertTriangle className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{stats.overdueTasks}</div>
            </CardContent>
          </Card>
        </div>

        {/* Active Projects Grid */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Active Projects</h2>
            <Button variant="outline" onClick={() => navigate("/projects")}>
              View All Projects
            </Button>
          </div>
          
          {projectsLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardHeader>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-200 rounded w-1/2"></div>
                  </CardHeader>
                  <CardContent>
                    <div className="h-2 bg-gray-200 rounded mb-2"></div>
                    <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : projects?.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {projects.slice(0, 6).map((project: any) => {
                const progress = getProjectProgress(project);
                const projectStatus = getProjectStatus(project);
                
                return (
                  <Card key={project.id} className="hover:shadow-md transition-shadow cursor-pointer">
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div>
                          <CardTitle className="text-lg">{project.name}</CardTitle>
                          <CardDescription className="line-clamp-2">{project.description}</CardDescription>
                        </div>
                        <Badge className={projectStatus.color}>
                          {projectStatus.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span>Progress</span>
                          <span>{progress}%</span>
                        </div>
                        <Progress value={progress} />
                      </div>
                      
                      <div className="flex justify-between items-center">
                        <div className="flex gap-2">
                          {project.project_contacts?.slice(0, 3).map((pc: any) => (
                            <Badge key={pc.contacts.id} variant="outline" className="text-xs">
                              {pc.contacts.name}
                            </Badge>
                          ))}
                          {project.project_contacts?.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{project.project_contacts.length - 3}
                            </Badge>
                          )}
                        </div>
                        
                        <div className="flex gap-1">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => navigate(`/projects/${project.id}/tracking`)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Track
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <Card>
              <CardContent className="text-center py-8">
                <h3 className="text-lg font-medium mb-2">No Projects Yet</h3>
                <p className="text-muted-foreground mb-4">Create your first project to get started.</p>
                <Button onClick={() => setNewProjectOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Project
                </Button>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Recent Project Tasks */}
        <div>
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Recent Project Tasks</h2>
            <Button variant="outline" onClick={() => navigate("/project-tracking")}>
              View Project Tracking
            </Button>
          </div>
          
          <Card>
            <CardContent className="p-0">
              {recentProjectTasks?.length > 0 ? (
                <div className="divide-y">
                  {recentProjectTasks.slice(0, 5).map((task: any) => (
                    <div key={task.id} className="p-4 flex items-center justify-between hover:bg-muted/50">
                      <div className="flex-1">
                        <h4 className="font-medium">{task.title}</h4>
                        <p className="text-sm text-muted-foreground">
                          {task.project_name && `${task.project_name} • `}
                          {task.assignee?.full_name && `Assigned to ${task.assignee.full_name} • `}
                          {task.due_date && `Due ${new Date(task.due_date).toLocaleDateString()}`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge variant={task.status === 'completed' ? 'default' : 'secondary'}>
                          {task.status || 'To Do'}
                        </Badge>
                        {task.priority && (
                          <Badge variant="outline">{task.priority}</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <p className="text-muted-foreground">No recent project tasks found.</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ProjectDashboard;
